import { ApolloError } from 'apollo-client'
import isEmpty from 'lodash/isEmpty'
import * as React from 'react'
import { Mutation, MutationFunction } from 'react-apollo'

import Form from 'main/components/common/Form'
import { PrivacyDisclaimer } from 'main/components/common/shared/PrivacyDisclaimer'
import { CredentialsFields } from 'main/gql/credentials'
import {
  CREATE_PARENT_USER_MUTATION,
  CreateParentUser,
  CreateParentUserVariables,
} from 'main/gql/user'
import { NativeLink, Text } from 'main/ui'
import { activateGoogleOptimize } from 'main/util/googleOptimize'
import { UserSignUpSource } from 'main/util/user'
import segment from 'util/analytics/segment'

import { ExistingUser } from '../../common'
import { IGoogleOptimize, ITrackingDataExternalSchoolId, ITrackingDataSchoolId } from '../../types'
import MutationError, { ErrorType } from './Error'
import Fields, { initialParams, validate } from './Fields'
import {
  DirectorContainer,
  ParentContainer,
  ParentSmallContainer,
  StyledButton,
} from './InitialParentSignUpForm.styles'
import { IParams, IValidation } from './types'

interface IProps {
  cta?: string
  googleOptimize?: IGoogleOptimize
  source: UserSignUpSource
  subtext?: string
  trackingData: ITrackingDataSchoolId | ITrackingDataExternalSchoolId
  onComplete: (credentials: CredentialsFields) => void
  onRequestDirectorSignUp?: () => void
  onRequestLogin: () => void
}

export default function InitialParentSignUpForm(props: IProps) {
  const {
    cta = 'Sign up',
    googleOptimize,
    source,
    subtext,
    trackingData,
    onComplete,
    onRequestDirectorSignUp,
    onRequestLogin,
  } = props

  const [params, setParams] = React.useState<IParams>(initialParams())
  const [validation, setValidation] = React.useState<IValidation>({})
  const [error, setError] = React.useState<ErrorType | null>(null)

  React.useEffect(() => activateGoogleOptimize(), [JSON.stringify(googleOptimize)])

  return (
    <Mutation<CreateParentUser, CreateParentUserVariables>
      mutation={CREATE_PARENT_USER_MUTATION}
      onCompleted={data => handleCompleted(data, trackingData, onComplete)}
      onError={apolloError => handleError(apolloError, setValidation, setError)}
    >
      {(mutate, { loading }) => (
        <Form onSubmit={() => handleSubmit(source, params, setValidation, setError, mutate)}>
          {onRequestDirectorSignUp && (
            <DirectorContainer>
              <Text>Looking to start a Wonderschool instead?&nbsp;</Text>
              <NativeLink onClick={() => onRequestDirectorSignUp()}>Click here.</NativeLink>
            </DirectorContainer>
          )}

          {subtext && (
            <ParentSmallContainer>
              <Text data-go={googleOptimize && googleOptimize.subtext}>{subtext}</Text>
            </ParentSmallContainer>
          )}

          <ParentContainer>
            <Fields
              params={params}
              validation={validation}
              onChange={changes => handleChange(changes, setParams)}
            />

            {error && (
              <ParentSmallContainer>
                <MutationError type={error} onRequestLogin={onRequestLogin} />
              </ParentSmallContainer>
            )}
          </ParentContainer>

          <StyledButton
            kind="regular"
            disabled={loading}
            full
            data-go={googleOptimize && googleOptimize.submitButton}
            data-pendo="parentSignUpButton"
            data-cy-hint="parent-sign-up"
          >
            {loading ? 'Signing up...' : cta}
          </StyledButton>

          <ParentContainer>
            <PrivacyDisclaimer />
          </ParentContainer>

          <ExistingUser onRequestLogin={onRequestLogin} />
        </Form>
      )}
    </Mutation>
  )
}

function handleChange(
  changes: Partial<IParams>,
  setParams: React.Dispatch<React.SetStateAction<IParams>>,
) {
  setParams(params => ({ ...params, ...changes }))
}

function handleSubmit(
  source: string,
  params: IParams,
  setValidation: (validation: IValidation) => void,
  setError: (error: ErrorType | null) => void,
  mutate: MutationFunction<CreateParentUser, CreateParentUserVariables>,
) {
  const validation = validate(params)
  setValidation(validation)
  setError(null)
  if (!isEmpty(validation)) {
    return
  }

  const { email, firstName } = params

  mutate({
    variables: {
      user: {
        email: email.trim(),
        family: {},
        firstName,
        parentTosAgreed: true,
        signUpSource: source,
      },
    },
  })
}

function handleCompleted(
  data: CreateParentUser,
  trackingData: ITrackingDataSchoolId | ITrackingDataExternalSchoolId,
  onComplete: (credentials: CredentialsFields) => void,
) {
  segment.track('New Parent Lead', trackingData)

  const credentials = data.createParentUser && data.createParentUser.credentials
  if (!credentials) {
    throw new Error('no credentials')
  }

  onComplete(credentials)
}

function handleError(
  apolloError: ApolloError,
  setValidation: (validation: IValidation) => void,
  setError: (error: ErrorType) => void,
) {
  let error: ErrorType | null = null
  const validation: IValidation = {}
  const messages = apolloError.graphQLErrors.map(e => e.message)

  if (messages.includes('email has already been taken')) {
    error = ErrorType.EmailExists
  }

  if (messages.includes('email is invalid') || messages.includes('email has invalid format')) {
    validation.email = 'Please enter a valid email'
  }

  if (messages.includes('first_name cannot include an @ symbol')) {
    validation.firstName = 'First name cannot have an @ symbol'
  }

  setValidation(validation)
  if (error) {
    setError(error)
  } else if (isEmpty(validation)) {
    setError(ErrorType.Generic)
  }
}
